/**
 * The level a site card asked for (owner, 2026-10-12: the Essentials / Standard / Thorough cards on cleancleaver.com click through to
 * sign-up). The link is …/app/?signup=1&level=standard (core cadence.ts levelFromQuery). Kept here the moment the app loads, like the
 * buy intent, so it survives Google's sign-in window, the email code and a reload; setup then starts the new place at that level.
 * Only a real level id is kept (anything else is dropped, never trusted), for one day, and it is cleared once the place is made.
 */
import { LEVEL_PARAM, levelFromParam, levelFromQuery, type LogLevel } from "@logwave/core";

export const LEVEL_KEY = "cc.level.intent";
export const LEVEL_TTL_MS = 24 * 3_600_000;

function safeStorage(): Storage | undefined {
  try { return typeof localStorage === "undefined" ? undefined : localStorage; } catch { return undefined; }
}
let memory: { level: LogLevel; at: string } | null = null;

/** A visit from a level card: keep the level. Returns it, or null for any other visit (or an unknown level). */
export function captureLevel(search: string, storage: Storage | undefined = safeStorage(), now = new Date()): LogLevel | null {
  const level = levelFromQuery(search);
  if (!level) return null;
  memory = { level, at: now.toISOString() };
  try { storage?.setItem(LEVEL_KEY, JSON.stringify(memory)); } catch { /* this page load only */ }
  return level;
}

/** The waiting level, re-checked on every read (a value stored by hand is dropped), or null when none or too old. */
export function pendingLevel(storage: Storage | undefined = safeStorage(), now = new Date()): LogLevel | null {
  let v: { level?: unknown; at?: unknown } | null;
  try { v = storage ? JSON.parse(storage.getItem(LEVEL_KEY) ?? "null") : memory; } catch { v = memory; }
  if (!v || typeof v.at !== "string" || typeof v.level !== "string") return null;
  const at = Date.parse(v.at);
  if (!Number.isFinite(at) || now.getTime() - at > LEVEL_TTL_MS || at - now.getTime() > 60_000) return null;
  return levelFromParam(v.level);
}

export function clearLevel(storage: Storage | undefined = safeStorage()): void {
  memory = null;
  try { storage?.removeItem(LEVEL_KEY); } catch { /* ignore */ }
}

/** The address without the level parameter (signup=1 stays). */
export function withoutLevelParam(search: string): string {
  const p = new URLSearchParams(search);
  p.delete(LEVEL_PARAM);
  const s = p.toString();
  return s ? `?${s}` : "";
}
